import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  TextField,
  Tooltip,
  Chip,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";
import * as FeatureData from "../../../config/features";
import {
  SearchDialogContainer,
  SearchDialogHeader,
  AnimatedAutocomplete,
  SearchContainer,
  ButtonStyle,
} from "./style";
import PropTypes from "prop-types";
import MovingIcon from "@mui/icons-material/Moving";

// Create a map to identify which feature category an item belongs to
const FEATURE_CATEGORIES = {
  HOME_FEATURES: "Home",
  RACK_PRICING_FEATURES: "Rack Pricing",
  RACK_FEATURES: "Rack",
  BID_PRICING_FEATURES: "Bid Pricing",
  BID_FEATURES: "Bid",
  REPORTS_FEATURES: "Reports",
  SETUP_FEATURES: "Setup",
};

// Flatten all features and add their category
const ALL_FEATURES = Object.entries(FeatureData).flatMap(
  ([category, features]) => {
    return features.map((feature) => ({
      ...feature,
      category: FEATURE_CATEGORIES[category] || "Other",
    }));
  }
);

// Sort features alphabetically
const SORTED_FEATURES = [...ALL_FEATURES].sort((a, b) =>
  a.title.localeCompare(b.title)
);

const SearchLayout = ({ isOpen, children, setIsOpen }) => {
  const style = { background: "none" };
  return (
    <SearchContainer style={isOpen ? null : style}>
      <Tooltip
        title="Toggle Search"
        placement="left"
        arrow
        enterDelay={0}
        leaveDelay={0}
      >
        <button style={ButtonStyle} onClick={() => setIsOpen((prev) => !prev)}>
          <SearchIcon />
        </button>
      </Tooltip>
      {children}
    </SearchContainer>
  );
};

const GlobalSearchBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const allOptions = useMemo(() => SORTED_FEATURES, []);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = useCallback(
    (event, value) => {
      if (value?.route) {
        setIsOpen(false);
        navigate(value.route);
      }
    },
    [navigate]
  );

  const handleClose = () => {
    setInputValue("");
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => inputRef.current?.focus(), 100);
      setInputValue("");
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const inputWidth = isMobile ? "100%" : 240;

  const autoComplete = (
    <AnimatedAutocomplete
      disabled={!isOpen}
      className={isOpen ? "visible" : ""}
      sx={{
        width: isOpen ? inputWidth : 0,
      }}
      options={allOptions}
      freeSolo
      getOptionLabel={(option) => option.title || ""}
      groupBy={(option) => option.category}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={handleClose}
      onChange={(event, value) => {
        handleSelect(event, value);
      }}
      filterOptions={(options, state) =>
        options.filter(
          (opt) =>
            opt.title.toLowerCase().includes(state.inputValue.toLowerCase()) ||
            opt.description
              .toLowerCase()
              .includes(state.inputValue.toLowerCase()) ||
            opt.category.toLowerCase().includes(state.inputValue.toLowerCase())
        )
      }
      slotProps={{
        paper: {
          sx: {
            width: isMobile ? "100%" : "calc(100% + 50px)",
            ml: isMobile ? "0px" : "-50px",
          },
        },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box
            sx={{ fontSize: 12, height: "auto", py: 1 }}
            component="li"
            key={key}
            {...rest}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}
            >
              <MovingIcon sx={{ marginRight: 1 }} />
              <Typography sx={{ flexGrow: 1 }}>{option.title}</Typography>
              <Chip
                size="small"
                label={option.category}
                sx={{
                  ml: 1,
                  fontSize: "0.7rem",
                  height: "20px",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.08)",
                }}
              />
            </Box>
          </Box>
        );
      }}
      noOptionsText="No records found"
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          variant="standard"
          placeholder="Search…"
          slotProps={{
            input: {
              ...params.InputProps,
              disableUnderline: true,
              sx: {
                color: !isMobile ? "white" : undefined,
              },
            },
          }}
        />
      )}
    />
  );

  if (isMobile) {
    return (
      <SearchLayout isOpen={isOpen} setIsOpen={setIsOpen}>
        <SearchDialogContainer
          open={isOpen}
          onClose={handleClose}
          fullWidth
          maxWidth="sm"
        >
          <SearchDialogHeader>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: "18px",
                color: theme.palette.mode === "dark" ? "#fff" : "#333",
              }}
            >
              Search
            </Typography>
            <IconButton
              onClick={handleClose}
              aria-label="close"
              sx={{
                color: theme.palette.mode === "dark" ? "#fff" : "#666",
                padding: "4px",
              }}
            >
              <CloseIcon />
            </IconButton>
          </SearchDialogHeader>
          <Box sx={{ px: 2, py: 2 }}>{autoComplete}</Box>
        </SearchDialogContainer>
      </SearchLayout>
    );
  }

  return (
    <SearchLayout isOpen={isOpen} setIsOpen={setIsOpen}>
      {autoComplete}
    </SearchLayout>
  );
};

// PropTypes for SearchLayout
SearchLayout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  children: PropTypes.node.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default GlobalSearchBar;
